import { create } from "zustand";
import { LOG } from "../utils/logUtil";
import axios from "axios";
import { useAppLoading } from "./useAppLoading";
import { useAlert } from "./useAlert";
import { IUser } from "./useUsers";
import { IVoucher } from "./useVouchers";

export interface IUserChallenge {
  _id: string;
  user: string | IUser;
  challenge: string | IChallenge;
  progress: {
    dropCount: number;
    parcelCount: number;
    parcelCompletionCount?: number;
    parcelIssuedRewardCount?: number;
    dropValue: number;
    streakCount: number;
    donationCount: number;
    lastDropDate: Date;
    completed: boolean;
    voucherIssued: string | IVoucher | null;
  };
  updatedAt: Date;
  createdAt: Date;
}

/**
 * One earned cycle of a challenge.
 *
 * A recurring challenge has no single completed state — it is finished once
 * per cycle, and each cycle either paid out its reward or is still waiting on
 * voucher stock. That is why this is a row rather than a flag on the progress
 * document: support needs to see which cycle is unpaid, not just that
 * something is.
 */
export interface IChallengeCompletion {
  _id: string;
  user: {
    _id: string;
    first_name?: string;
    last_name?: string;
    email?: string;
  } | null;
  challenge:
    | string
    | {_id: string; en_title?: string; nl_title?: string; allowMultipleCompletions?: boolean};
  cycleNumber: number;
  targetSnapshot: number;
  /** Parcel rows only. */
  thresholdParcelCount?: number;
  /**
   * Every other type's counterpart of thresholdParcelCount, in the unit of
   * the condition: cents for the value types.
   */
  thresholdValue?: number;
  /** Absent on parcel rows written before other types could repeat: VOUCHER. */
  rewardType?: "VOUCHER" | "DROPPIES" | "CASH";
  /** The number of Droppies for DROPPIES, cents for CASH. */
  rewardValueSnapshot?: number;
  triggeringEvent?: {
    source: "manual" | "tomra" | "beevision" | "donation";
    sourceId: string;
  };
  /** Always ISSUED for DROPPIES and CASH rows. */
  rewardStatus: "PENDING_STOCK" | "ISSUED";
  /** Null for DROPPIES and CASH rows. */
  voucherIssued: {_id: string; code?: string; value?: number} | null;
  earnedAt: string;
  issuedAt: string | null;
}

export interface IChallenge {
  _id: string;
  en_title: string;
  nl_title: string;
  en_description: string;
  nl_description: string;
  partnerId: string;
  /**
   * Whether one customer may finish this more than once. Not a schedule:
   * every time the target is reached again is another cycle and the
   * remainder carries over. Only on REPEATABLE_CHALLENGE_TYPES, and a parcel
   * challenge only with a VOUCHER reward.
   */
  allowMultipleCompletions?: boolean;
  /**
   * How many customers have progress on it. Listed challenges only; a played
   * challenge cannot be deleted, so the list offers no Delete for it.
   */
  participantCount?: number;
  /** Usable challenge vouchers: the partner's whole stock, and the part of it
   *  pinned to this challenge. Counted by the same rule the claim uses. */
  partnerVoucherCount?: number;
  challengeVoucherCount?: number;
  /** Players: how many finished it at least once, and how many started but
   *  have not. A repeating challenge counts one earned cycle as finished. */
  completedCount?: number;
  inProgressCount?: number;
  startsAt: Date;
  endsAt: Date;
  status: "ACTIVE" | "INACTIVE" | "COMPLETED";
  conditions: {
    type:
      | "FIRST_DROP"
      | "DROP_COUNT"
      | "DROP_VALUE"
      | "ITEM_TYPE"
      | "ITEM_TYPE_COUNT"
      | "ITEM_TYPE_DROP_COUNT"
      | "ITEM_TYPE_VALUE"
      | "TOMRA_ITEM_TYPE"
      | "TOMRA_ITEM_TYPE_COUNT"
      | "TOMRA_ITEM_TYPE_DROP_COUNT"
      | "TOMRA_ITEM_TYPE_VALUE"
      | "DONATION"
      | "INVITE"
      | "STREAKS"
      | "POSTAL_CODE"
      | "COMPLETE_PROFILE"
      | "PARCEL_COUNT";
    minDrops?: number;
    minItems?: number;
    minValue?: number;
    itemType?: string;
    tomraItemType?: string;
    streakType?: "DAILY" | "WEEKLY" | "MONTHLY";
    streakCount?: number;
    donationCount?: number;
    /**
     * DONATION only, in cents. A donation counts only if it is at least
     * this; absent or 0 means any amount. At least 100 once it repeats.
     */
    minDonationAmount?: number;
    /** PARCEL_COUNT only — any one of these carriers counts (OR). */
    carrierPartnerIds?: string[];
    parcelDirection?: "PICKUP" | "DROPOFF" | "EITHER";
    minParcelCount?: number;
  };
  backgroundImage?: string;
  /** Square picture on the side of challenge cards in the app. Without it the
   *  cards use backgroundImage. */
  cardImage?: string | null;
  voucherBackgroundImage?: string;
  cities?: string[];
  /** When set, the challenge runs only at these stores and cities are ignored. */
  locationIds?: string[] | null;
  rewardType: "VOUCHER" | "DROPPIES" | "CASH";
  rewardValue?: number;
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Condition types a challenge can repeat on. The rest (FIRST_DROP, ITEM_TYPE,
 * TOMRA_ITEM_TYPE, INVITE, POSTAL_CODE, COMPLETE_PROFILE) are one-off by
 * nature. Whether this environment allows one right now is a separate
 * answer: recurringChallengeTypes.
 */
export const REPEATABLE_CHALLENGE_TYPES: IChallenge["conditions"]["type"][] = [
  "PARCEL_COUNT",
  "DROP_COUNT",
  "DROP_VALUE",
  "ITEM_TYPE_COUNT",
  "ITEM_TYPE_DROP_COUNT",
  "ITEM_TYPE_VALUE",
  "TOMRA_ITEM_TYPE_COUNT",
  "TOMRA_ITEM_TYPE_DROP_COUNT",
  "TOMRA_ITEM_TYPE_VALUE",
  "STREAKS",
  "DONATION",
];

interface IStore {
  /** State */
  allCompletedChallenges: IUserChallenge[];
  /**
   * What the backend currently permits. Null until fetched, so the UI can
   * tell "not asked yet" from "not allowed" and does not flash a control
   * disabled before the answer arrives.
   */
  recurringChallengesEnabled: boolean | null;
  /**
   * The condition types a repeating challenge may be saved with on this
   * environment. Null until fetched, like the flag above; empty when nothing
   * may repeat or the check failed.
   */
  recurringChallengeTypes: string[] | null;
  challengeCompletions: IChallengeCompletion[];
  challengeCompletionsTotal: number;
  hasNextChallengeCompletions: boolean;
  allPartnerChallenges: IChallenge[];
  onePartnerChallenge: IChallenge | null;
  createdPartnerChallenge: string | null;
  updatedPartnerChallenge: string | null;
  deletedPartnerChallenge: string | null;
  hasNextCompletedChallenges: boolean;
  /** Callbacks */
  getAllCompletedChallenges: (
    page: number,
    limit: number,
    challengeId: string
  ) => void;
  getChallengeCapabilities: () => void;
  getChallengeCompletions: (
    challengeId: string,
    page: number,
    limit: number,
    rewardStatus?: "PENDING_STOCK" | "ISSUED"
  ) => void;
  clearChallengeCompletions: () => void;
  getAllPartnerChallenges: () => void;
  getOnePartnerChallenge: (id: string) => void;
  createPartnerChallenge: (challenge: IChallenge) => void;
  updatePartnerChallenge: (id: string, challenge: IChallenge) => void;
  deletePartnerChallenge: (id: string) => void;
  clearCreatedPartnerChallenge: () => void;
  clearUpdatedPartnerChallenge: () => void;
  clearDeletedPartnerChallenge: () => void;
  clearAllCompletedChallenges: () => void;
}

export const usePartnerChallenges = create<IStore>()((set, get) => ({
  /** State */
  allCompletedChallenges: [],
  recurringChallengesEnabled: null,
  recurringChallengeTypes: null,
  challengeCompletions: [],
  challengeCompletionsTotal: 0,
  hasNextChallengeCompletions: false,
  allPartnerChallenges: [],
  onePartnerChallenge: null,
  createdPartnerChallenge: null,
  updatedPartnerChallenge: null,
  deletedPartnerChallenge: null,
  hasNextCompletedChallenges: false,
  /** Callbacks */
  getAllCompletedChallenges: async (
    page: number,
    limit: number,
    challengeId: string
  ) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.get(
        `/partners/user-challenges/completed?page=${page}&limit=${limit}&challengeId=${challengeId}`
      );
      if (response?.data) {
        set((state) => ({
          ...state,
          hasNextCompletedChallenges: response?.data?.length === limit,
          allCompletedChallenges:
            page === 1
              ? response?.data
              : [...state.allCompletedChallenges, ...response?.data],
        }));
      }
    } catch (error) {
      LOG.error("Error on fetching completed challenges", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  getChallengeCapabilities: async () => {
    try {
      const response = await axios.get("/partners/challenges/capabilities");
      const recurringChallengesEnabled = Boolean(
        response?.data?.recurringChallengesEnabled
      );
      const types = response?.data?.recurringChallengeTypes;
      // An older backend sends only the parcel flag, and parcel was the one
      // type it could repeat.
      const fallbackTypes = recurringChallengesEnabled ? ["PARCEL_COUNT"] : [];
      set({
        recurringChallengesEnabled,
        recurringChallengeTypes: Array.isArray(types) ? types : fallbackTypes,
      });
    } catch (error) {
      // Fail closed, exactly as the backend does: an unreachable capability
      // check must not leave a control enabled that the server will refuse.
      LOG.error("Error on fetching challenge capabilities", { error });
      set({ recurringChallengesEnabled: false, recurringChallengeTypes: [] });
    }
  },
  getChallengeCompletions: async (
    challengeId: string,
    page: number,
    limit: number,
    rewardStatus?: "PENDING_STOCK" | "ISSUED"
  ) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const query = new URLSearchParams({
        challengeId,
        page: String(page),
        limit: String(limit),
      });
      if (rewardStatus) query.set("rewardStatus", rewardStatus);
      const response = await axios.get(
        `/partners/user-challenges/completions?${query.toString()}`
      );
      if (response?.data) {
        const items: IChallengeCompletion[] = response.data.items ?? [];
        set((state) => ({
          ...state,
          challengeCompletionsTotal: response.data.total ?? items.length,
          hasNextChallengeCompletions: Boolean(response.data.hasNextPage),
          // Page 1 replaces rather than appends, so switching the filter does
          // not leave the previous filter's rows above the new ones.
          challengeCompletions:
            page === 1 ? items : [...state.challengeCompletions, ...items],
        }));
      }
    } catch (error) {
      LOG.error("Error on fetching challenge completions", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  clearChallengeCompletions: () => {
    set({
      challengeCompletions: [],
      challengeCompletionsTotal: 0,
      hasNextChallengeCompletions: false,
    });
  },
  getAllPartnerChallenges: async () => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.get("/partners/challenges");
      if (response?.data) {
        set({ allPartnerChallenges: response.data });
      }
    } catch (error) {
      LOG.error("Error on fetching Partner challenges", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  getOnePartnerChallenge: async (id: string) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.get(`/partners/challenges/${id}`);
      if (response?.data) {
        set({ onePartnerChallenge: response.data });
      }
    } catch (error) {
      LOG.error("Error on fetching one Partner challenge", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  createPartnerChallenge: async (badge: IChallenge) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.post("/partners/challenges", badge);
      if (response?.data) {
        set({ createdPartnerChallenge: response.data?._id });
      }
    } catch (error: any) {
      LOG.error("Error on creating a Partner Challenge", { error });
      // The server's own message is the useful one — it names the rule that
      // was broken. Logging it to the console and leaving the form silent
      // reads to the operator as "the button does nothing".
      useAlert.getState().setAlert({
        isOpen: true,
        severity: "error",
        // The shared interceptor rejects with the response itself, so the
        // body sits at error.data; the nested shape is kept as a fallback.
        message:
          error?.data?.message ??
          error?.response?.data?.message ??
          "Could not save the challenge. Please try again.",
      });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  updatePartnerChallenge: async (id: string, badge: IChallenge) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.put(`/partners/challenges/${id}`, badge);
      if (response?.data) {
        set((state) => ({
          updatedPartnerChallenge: id,
          allPartnerChallenges: state.allPartnerChallenges.map((c) =>
            c._id === id ? { ...c, ...badge } : c
          ),
        }));
      }
    } catch (error: any) {
      LOG.error("Error on updating a Partner Challenge", { error });
      useAlert.getState().setAlert({
        isOpen: true,
        severity: "error",
        // The shared interceptor rejects with the response itself, so the
        // body sits at error.data; the nested shape is kept as a fallback.
        message:
          error?.data?.message ??
          error?.response?.data?.message ??
          "Could not save the challenge. Please try again.",
      });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  deletePartnerChallenge: async (id: string) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.delete(`/partners/challenges/${id}`);
      if (response?.data) {
        set((state) => ({
          allPartnerChallenges: state.allPartnerChallenges.filter(
            (challenge) => challenge._id !== response.data?._id
          ),
          deletedPartnerChallenge: response.data?._id,
        }));
      }
    } catch (error: any) {
      LOG.error("Error on deleting a Partner challenge", { error });
      // A challenge people have already played cannot be deleted, and the
      // server says so. Without this the button just did nothing.
      useAlert.getState().setAlert({
        isOpen: true,
        severity: "error",
        // The shared interceptor rejects with the response itself, so the
        // body sits at error.data; the nested shape is kept as a fallback.
        message:
          error?.data?.message ??
          error?.response?.data?.message ??
          "Could not delete the challenge. Please try again.",
      });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  clearCreatedPartnerChallenge: () => {
    set({ createdPartnerChallenge: null });
  },
  clearUpdatedPartnerChallenge: () => {
    set({ updatedPartnerChallenge: null });
  },
  clearDeletedPartnerChallenge: () => {
    set({ deletedPartnerChallenge: null });
  },
  clearAllCompletedChallenges: () => {
    set({ allCompletedChallenges: [] });
  },
}));
